import { type JSX } from 'react';

export interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  /** Stroke + gradient color. CSS var or hex. Defaults to volt-500. */
  color?: string;
  className?: string;
}

/**
 * Single-series area+line sparkline. Used in dashboard KPI cards.
 *
 * Renders inline SVG with a soft gradient fill underneath the stroke.
 * Returns null when there are fewer than two data points so the card
 * doesn't show a misleading flat line.
 */
export function Sparkline({
  data,
  width = 120,
  height = 36,
  color = 'var(--volt-500)',
  className,
}: SparklineProps): JSX.Element | null {
  if (data.length < 2) return null;

  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;

  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return [x, y] as const;
  });

  const linePath = points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(' ');
  const fillPath = `${linePath} L${width},${height} L0,${height} Z`;

  // Per-instance gradient id so two sparklines on the same page don't collide.
  const gradId = `spark-grad-${Math.random().toString(36).slice(2, 8)}`;

  return (
    <svg width={width} height={height} className={className} aria-hidden="true">
      <defs>
        <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fillPath} fill={`url(#${gradId})`} />
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
